import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Sym from './Sym';
import Logo from './Logo';
import { Menu, MenuItem, MenuSeparator } from './Menu';
import { SegmentedControl } from './Tabs';
import { usePageTitleValue } from './PageTitleContext';
import { useTheme } from './ThemeContext';
import { THEME_OPTIONS } from './theme';
import CompanySwitcher from './CompanyPicker';
import { esRutaDeEmpresa, useCompany } from './CompanyContext';
import { initialsOf, mobileNavFor, navGroupsFor, pageMetaFor, ROLE_LABEL } from './nav';
import authService from '../../utils/authService';
import AlertService from '../../utils/alertService';

/**
 * Shell de la aplicación (rediseño jul-2026): menú lateral + header con título
 * contextual + barra inferior en móvil. Sustituye al Header de pestañas.
 *
 * Diferencias deliberadas contra el prototipo (documentadas en el plan):
 *   - NO hay buscador global: no existe un endpoint de búsqueda transversal.
 *   - NO hay selector "Ver la app como": era un artificio del prototipo para
 *     demostrar los tres roles; aquí el rol viene del JWT.
 *   - Se añade un cajón lateral en móvil para no perder accesos que la barra
 *     inferior deja fuera.
 *
 * El selector de tema (claro · automático · oscuro) vive donde lo pone el
 * diseño: en el header. En pantallas chicas se oculta —ahí el header solo tiene
 * espacio para el título y la cuenta— y se llega por Mi cuenta ▸ Preferencias,
 * que también lo trae.
 */

const isActivePath = (pathname, to) => {
  if (pathname === to) return true;
  // Las subrutas marcan activo a su sección (p. ej. la ficha de un prospecto).
  return pathname.startsWith(`${to}/`);
};

/**
 * Identidad de quien está dentro (nombre + rol).
 *
 * NO es un acceso: el camino a la cuenta es el menú del header (y, en móvil, la
 * barra inferior). Antes este bloque enlazaba a /app/account y convivía con el
 * ítem "Mi cuenta" del header, así que había dos caminos al mismo sitio y un
 * engrane que prometía una configuración aparte que no existe.
 */
const Identidad = ({ nombre, rol }) => (
  <div className="a2-account-btn a2-account-btn--estatico">
    <span className="a2-avatar" style={{ width: 32, height: 32, fontSize: '.82rem' }}>
      {initialsOf(nombre)}
    </span>
    <span style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <span className="a2-truncate" style={{ font: "700 .9rem 'Hanken Grotesk'", color: 'var(--heading)' }}>
        {nombre}
      </span>
      <span style={{ font: "500 .74rem 'Hanken Grotesk'", color: 'var(--muted)' }}>
        {ROLE_LABEL[rol] || 'Cuenta'}
      </span>
    </span>
  </div>
);

/** Lista de grupos + items; la comparten el sidebar de escritorio y el cajón móvil. */
const NavGroups = ({ groups, pathname, onNavigate }) => (
  <nav className="a2-nav">
    {groups.map((g) => (
      <div key={g.label}>
        <div className="a2-nav-label">{g.label}</div>
        <div className="a2-nav-items">
          {g.items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              onClick={onNavigate}
              className={['a2-nav-item', isActivePath(pathname, it.to) ? 'is-active' : ''].filter(Boolean).join(' ')}
              aria-current={isActivePath(pathname, it.to) ? 'page' : undefined}
            >
              <Sym name={it.icon} size={21} />
              {it.label}
            </Link>
          ))}
        </div>
      </div>
    ))}
  </nav>
);

const AppShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { mode, setMode } = useTheme();
  const { empresas } = useCompany();
  const [cambiarEmpresa, setCambiarEmpresa] = useState(false);

  const currentUser = authService?.getCurrentUser();
  const role = currentUser?.role;
  const titleOverride = usePageTitleValue();

  const groups = useMemo(() => navGroupsFor(role), [role]);
  const mobileItems = useMemo(() => mobileNavFor(role), [role]);

  const displayName = currentUser?.first_name && currentUser?.last_name
    ? `${currentUser.first_name} ${currentUser.last_name}`
    : currentUser?.profile?.firstName || currentUser?.first_name || currentUser?.username || 'Mi cuenta';

  // Se puede cambiar de empresa donde el contenido depende de ella y hay más de
  // una: con una sola empresa no habría nada que elegir.
  const puedeCambiarEmpresa = esRutaDeEmpresa(location.pathname) && (empresas?.length || 0) > 1;

  const [routeKicker, routeTitle] = pageMetaFor(location.pathname, role);
  const kicker = titleOverride?.kicker ?? routeKicker;
  const title = titleOverride?.title ?? routeTitle;

  // El cajón se cierra al cambiar de ruta y con Escape.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Mismo flujo de salida que la app actual: confirmación y logout del servicio.
  const handleLogout = async () => {
    setUserMenuOpen(false);
    const confirmed = await AlertService?.confirm(
      '¿Está seguro que desea cerrar sesión?',
      'Su sesión actual se cerrará y deberá iniciar sesión nuevamente para acceder al sistema.',
    );
    if (confirmed) authService?.logout();
  };

  return (
    <div className="a2-shell">
      {/* ── Menú lateral (escritorio) ─────────────────────────────────────── */}
      <aside className="a2-sidebar">
        <Link to="/app" className="a2-brand" aria-label="PaySlip — inicio">
          <Logo size={28} />
          <span className="a2-brand-tag">APP</span>
        </Link>
        <NavGroups groups={groups} pathname={location.pathname} />
        <Identidad nombre={displayName} rol={role} />
      </aside>

      {/* ── Cajón lateral (móvil) ─────────────────────────────────────────── */}
      {drawerOpen ? (
        <>
          <div className="as2-drawer-overlay" onClick={() => setDrawerOpen(false)} />
          <div className="as2-drawer" role="dialog" aria-modal="true" aria-label="Menú de navegación">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px 18px' }}>
              <Logo size={26} />
              <button
                type="button"
                aria-label="Cerrar menú"
                onClick={() => setDrawerOpen(false)}
                style={{
                  marginLeft: 'auto', width: 34, height: 34, borderRadius: 9, border: 'none',
                  background: 'var(--surface-2)', color: 'var(--muted)', cursor: 'pointer',
                  display: 'grid', placeItems: 'center',
                }}
              >
                <Sym name="close" size={20} />
              </button>
            </div>
            <NavGroups
              groups={groups}
              pathname={location.pathname}
              onNavigate={() => setDrawerOpen(false)}
            />
            <Identidad nombre={displayName} rol={role} />
          </div>
        </>
      ) : null}

      {/* ── Contenido ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <header className="a2-header">
          <button
            type="button"
            className="a2-burger"
            aria-label="Abrir menú"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <Sym name="menu" size={24} />
          </button>

          {/* El TÍTULO es el selector de empresa donde el contenido depende de
              ella (Panel y Sincronización SAT) y hay más de una que elegir. Un
              botón aparte con el nombre lo repetía —y truncado— justo al lado
              del título que ya lo dice entero. En el resto de pantallas el
              título es texto, sin más. */}
          <div style={{ minWidth: 0 }}>
            {kicker ? <div className="a2-kicker">{kicker}</div> : null}
            {puedeCambiarEmpresa ? (
              <h1 className="a2-title" style={{ margin: 0 }}>
                <button
                  type="button"
                  className="a2-title-btn"
                  onClick={() => setCambiarEmpresa(true)}
                  title="Cambiar de empresa"
                  aria-haspopup="dialog"
                >
                  <span className="a2-truncate">{title}</span>
                  <Sym name="unfold_more" size={20} className="a2-title-btn-ic" />
                </button>
              </h1>
            ) : (
              <h1 className="a2-title">{title}</h1>
            )}
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <SegmentedControl
              className="a2-hide-sm-flex"
              items={THEME_OPTIONS}
              value={mode}
              onChange={setMode}
              role="group"
              aria-label="Tema de la interfaz"
            />

            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)',
                  border: '1px solid var(--line-2)', borderRadius: 10, padding: '7px 10px',
                  cursor: 'pointer', color: 'var(--heading)',
                }}
              >
                <span className="a2-avatar" style={{ width: 26, height: 26, fontSize: '.7rem' }}>
                  {initialsOf(displayName)}
                </span>
                <span
                  className="a2-username a2-truncate"
                  style={{ font: "600 .88rem 'Hanken Grotesk'", maxWidth: 150 }}
                >
                  {displayName}
                </span>
                <Sym name="expand_more" size={18} style={{ color: 'var(--muted)' }} />
              </button>

              <Menu open={userMenuOpen} onClose={() => setUserMenuOpen(false)} align="right" width={244}>
                <MenuItem
                  icon="person"
                  onClick={() => { setUserMenuOpen(false); navigate('/app/account/profile'); }}
                >
                  Mi cuenta
                </MenuItem>
                <MenuSeparator />
                <MenuItem
                  icon="description"
                  onClick={() => { setUserMenuOpen(false); navigate('/terms-of-service'); }}
                >
                  Términos de Servicio
                </MenuItem>
                <MenuItem
                  icon="shield"
                  onClick={() => { setUserMenuOpen(false); navigate('/privacy-policy'); }}
                >
                  Política de Privacidad
                </MenuItem>
                <MenuSeparator />
                <MenuItem icon="logout" tone="danger" onClick={handleLogout}>
                  Cerrar sesión
                </MenuItem>
              </Menu>
            </div>
          </div>
        </header>

        <main className="a2-main">{children}</main>
      </div>

      {/* Cambiar de empresa (se abre tocando el título) */}
      <CompanySwitcher open={cambiarEmpresa} onClose={() => setCambiarEmpresa(false)} />

      {/* ── Barra inferior (móvil) ────────────────────────────────────────── */}
      <nav className="a2-mobilenav" aria-label="Navegación principal">
        {mobileItems.map((m) => (
          <Link
            key={m.to}
            to={m.to}
            className={['a2-mobilenav-item', isActivePath(location.pathname, m.to) ? 'is-active' : ''].filter(Boolean).join(' ')}
            aria-current={isActivePath(location.pathname, m.to) ? 'page' : undefined}
          >
            <Sym name={m.icon} size={23} />
            <span>{m.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default AppShell;
